<script>
	import { slide } from "svelte/transition";

	import model from "../../stores/modelStore";
	import { getTealiumID, registerPageComplete } from "../../tealium";
	import { projectSettings } from "../../config";
	import { isValid } from "../../validation";

	import Form from "../../components/Form.svelte";
	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";
	import FormRowColumn from "../../components/FormRowColumn.svelte";
	import TextInput from "../../components/TextInput.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import RadioGroup from "../../components/RadioGroup.svelte";
	import CheckboxButtonList from "../../components/CheckboxButtonList.svelte";
	import CheckboxButton from "../../components/CheckboxButton.svelte";
	import Address from "../../components/Address.svelte";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	import ContactName from "./components/ContactName.svelte";
	import ContactDetails from "./components/ContactDetails.svelte";
	import OtherNames from "./components/OtherNames.svelte";
	import Family from "./components/Family.svelte";

	export let disabled = null;
	export let pageName = null;
	export let pageTitle = null;
	export let persistUrl = null;
	export let lastVisitedPage = null;

	let buttons = null;

	let selectedPreferContactOptions = [];

	const handleSubmit = async (e) => {
		buttons.blockNext();

		let errors = isValid();

		if (!errors.length) {
			//  Get the preferred contact methods
			$model.preferPhoneContact = selectedPreferContactOptions.includes("Phone");
			$model.preferEmailContact = selectedPreferContactOptions.includes("Email");
			$model.preferPostContact = selectedPreferContactOptions.includes("Post");

			// Check the Postal Address
			if ($model.claimDetails.postalAddressDifferent != "Yes") {
				$model.contact.postalAddress = {};
			}

			let response = await model.saveModelAsync(persistUrl);

			if (!response.success) {
				console.log(displayError);
				//displayError(response);
			} else {
				await registerPageComplete(pageName, $model.contact.emailAddress, $model.contact.phoneNumber);

				buttons.next();
			}
		} else {
			errors[0].scrollIntoView({ behavior: "smooth" });
		}

		buttons.unblock();
	};

	if (!disabled) {
		$model.lastPageVisited = lastVisitedPage;
		document.title = pageTitle;

		$model.tealiumId = getTealiumID();
	}

	const initialise = () => {
		if ($model.preferPhoneContact) {
			selectedPreferContactOptions.push("Phone");
		}
		if ($model.preferEmailContact) {
			selectedPreferContactOptions.push("Email");
		}
		if ($model.preferPostContact) {
			selectedPreferContactOptions.push("Post");
		}
	};

	initialise();
</script>

<PageSection>
	<Form {disabled} id="form-personal-details" on:submit={handleSubmit}>
		<FormRowColumn rowClass="justify-content-center confirmation-hide">
			<h4 class="text-center">
				{@html projectSettings.classActionName}
			</h4>
			<h1 class="text-center">Who is filling out this form?</h1>
		</FormRowColumn>

		<hr class="primary" />

		<div class="confirmation-box-shadow">
			<FormRowColumn>
				<RadioGroup
					{disabled}
					required
					cols="6"
					name="formCompletionType"
					label="Are you completing this form by yourself or is someone helping you?"
					bind:value={$model.formCompletionType}
					optionList={[
						{ label: "Yourself", value: "Yourself" },
						{
							label: "Someone is assisting you",
							value: "Assisted"
						}
					]}
				/>
			</FormRowColumn>

			{#if $model.formCompletionType == "Assisted"}
				<div transition:slide|local>
					<p>
						You are allowed to have help filling out this form. If someone is helping you, that person needs
						to give us their details below.
					</p>

					<FormRowColumn>
						<TextInput
							maxlength="100"
							required
							{disabled}
							label="If you are filling out this form for the claimant, how do you know them?"
							bind:value={$model.assistant.relationshipToClaimant}
						/>
					</FormRowColumn>

					<ContactName
						required
						{disabled}
						id="assistant"
						bind:firstName={$model.assistant.firstName}
						firstNameLabel="Your first name"
						bind:surname={$model.assistant.surname}
						surnameLabel="Your last name"
					/>

					<ContactDetails
						{disabled}
						id="assistant"
						bind:phoneNumber={$model.assistant.phoneNumber}
						phoneNumberLabel="Your phone number"
						bind:emailAddress={$model.assistant.emailAddress}
						emailAddressLabel="Your email address"
					/>
				</div>
			{/if}

			<p>
				In the rest of the form, when we ask about "you", we are asking about the person completing the
				registration form (claimant) and not the person helping them.
			</p>

			<FormRowColumn rowClass="justify-content-center confirmation-hide">
				<h1 class="text-center">Part 1: Claimant's details</h1>
			</FormRowColumn>

			<ContactName
				id="claimant"
				required
				{disabled}
				bind:firstName={$model.contact.firstName}
				firstNameLabel="Your first name"
				bind:surname={$model.contact.surname}
				surnameLabel="Your last name"
			/>

			<FormRowColumn>
				<TextInput
					maxlength="50"
					{disabled}
					label="Your skin name (if you have one)"
					bind:value={$model.claimDetails.skinName}
				/>
			</FormRowColumn>

			<OtherNames
				label="Do you have any other names? (e.g maiden name)"
				bind:hasOtherNames={$model.claimDetails.hasOtherNames}
				bind:otherNames={$model.contact.otherNames}
				{disabled}
			/>

			<FormRow class="align-items-end">
				<FormColumn>
					<TextInput
						required
						{disabled}
						maxlength="50"
						label="Your date of birth"
						sublabel="If you do not know your exact date of birth, you can give us an approximate date of birth or an approximate year of birth"
						bind:value={$model.claimDetails.dateOfBirth}
					/>
				</FormColumn>
				<FormColumn>
					<TextInput
						{disabled}
						maxlength="1000"
						label="Your place of birth"
						sublabel="If you do not know your exact place of birth, you can give us an approximate place of birth"
						bind:value={$model.claimDetails.placeOfBirth}
					/>
				</FormColumn>
			</FormRow>

			<Family
				{disabled}
				motherLabel="Your mother's name"
				fatherLabel="Your father's name"
				siblingLabel="The names of your brothers and sisters"
				bind:mother={$model.claimDetails.mother}
				bind:father={$model.claimDetails.father}
				bind:siblings={$model.claimDetails.siblings}
			/>

			<Address
				{disabled}
				required
				label="Your Home address"
				idPrefix="home"
				bind:address={$model.contact.homeAddress}
			/>

			<FormRowColumn>
				<YesNoGroup
					required
					{disabled}
					name="postalAddressDifferent"
					label="Is your postal address different to your home address?"
					bind:value={$model.claimDetails.postalAddressDifferent}
				/>
			</FormRowColumn>

			{#if $model.claimDetails.postalAddressDifferent == "Yes"}
				<div transition:slide|local>
					<Address
						{disabled}
						required
						label="Your Postal address"
						idPrefix="postal"
						bind:address={$model.contact.postalAddress}
					/>
				</div>
			{/if}

			<ContactDetails
				{disabled}
				id="contact"
				bind:phoneNumber={$model.contact.phoneNumber}
				phoneNumberLabel="Your phone number"
				phoneNumberSublabel="(if you have one)"
				bind:emailAddress={$model.contact.emailAddress}
				emailAddressLabel="Your email address"
				emailAddressSublabel="(if you have one)"
			/>

			<FormRowColumn>
				<YesNoGroup
					{disabled}
					name="pd.isAboriginalTorresStraitIslander"
					label="Are you Aboriginal or Torres Strait Islander?"
					bind:value={$model.claimDetails.isAboriginalTorresStraitIslander}
				/>
			</FormRowColumn>

			<CheckboxButtonList
				{disabled}
				name="knowledgeOfWork"
				label="How do you want to be contacted?"
				bind:selected={selectedPreferContactOptions}
				optionList={[
					{ label: "Phone", value: "Phone" },
					{ label: "Email", value: "Email" },
					{ label: "Post", value: "Post" }
				]}
			/>

			<FormRowColumn>
				<RadioGroup
					{disabled}
					required
					cols="12"
					name="primaryContactPerson"
					label="If we cannot contact you directly, is there a support person (for example, a family member, friend or community contact) we can contact to speak with you?"
					sublabel="If you do not have any contact details, you must give us a contact for a support person so we can contact you."
					bind:value={$model.primaryContactPerson}
					optionList={[
						{
							label: "Yes - the same person who helped you fill out this from",
							value: "Assistant"
						},
						{
							label: "Yes - a different person",
							value: "Support Person"
						},
						{ label: "No", value: "Myself" }
					]}
				/>
			</FormRowColumn>

			{#if $model.primaryContactPerson == "Support Person"}
				<div transition:slide|local>
					<ContactName
						id="support"
						required
						{disabled}
						bind:firstName={$model.supportPerson.firstName}
						bind:surname={$model.supportPerson.surname}
					/>

					<ContactDetails
						{disabled}
						id="supportPerson"
						bind:phoneNumber={$model.supportPerson.phoneNumber}
						phoneNumberLabel="Phone number"
						bind:emailAddress={$model.supportPerson.emailAddress}
						emailAddressLabel="Email address"
					/>

					<FormRowColumn>
						<TextInput
							maxlength="100"
							{disabled}
							label="How you know your support person"
							bind:value={$model.supportPerson.relationshipToClaimant}
						/>
					</FormRowColumn>

					<Address
						{disabled}
						label="Postal address"
						idPrefix="s-postal"
						bind:address={$model.supportPerson.postalAddress}
					/>
				</div>
			{/if}

			<FormRowColumn>
				<YesNoGroup
					required
					{disabled}
					name="agreeToShineInformation"
					label="Do you agree to Shine Lawyers giving information and asking for information about your registration/s in this form to your support person:"
					bind:value={$model.agreeToShineInformation}
				/>
			</FormRowColumn>

			<p class="label mt-4">What language do you or your support person want to talk to us in?</p>

			<FormRow>
				<FormColumn>
					<CheckboxButton
						{disabled}
						label="English"
						name="englishLanguage"
						bind:checked={$model.claimDetails.englishLanguage}
					/>
				</FormColumn>
				<FormColumn>
					<CheckboxButton
						{disabled}
						label="Language"
						name="otherLanguage"
						bind:checked={$model.claimDetails.otherLanguage}
					/>
				</FormColumn>
			</FormRow>

			{#if $model.claimDetails.otherLanguage}
				<div transition:slide|local>
					<FormRow>
						<FormColumn>
							<TextInput {disabled} label="Language" bind:value={$model.claimDetails.laguange} />
						</FormColumn>
						<FormColumn>
							<TextInput {disabled} label="Community" bind:value={$model.claimDetails.community} />
						</FormColumn>
					</FormRow>
				</div>
			{/if}
		</div>

		<FormRowColumn rowClass="confirmation-hide">
			<NavigationButtons bind:buttons />
		</FormRowColumn>
	</Form>
</PageSection>

<style>
</style>
